import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon } from '@mui/icons-material';
import { CartesianGrid, LabelList, ResponsiveContainer, Scatter, ScatterChart, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs';
import { ApplyPicksDialog } from '../components/tests/ApplyPicksDialog';
import { CaseMatrix, cellStatus } from '../components/tests/CaseMatrix';
import { ImproveWithLearning } from '../components/tests/ImproveWithLearning';
import { SectionTitle, StatCard } from '../components/performance/common';
import { PicksCards, Score, modelLabel } from '../components/tests/common';
import { errorText } from '../components/tests/dialogs';
import { ModelComparisonTable, rowsFromSummary } from '../components/tests/ModelComparison';
import { RunStatus } from '../components/tests/TargetTests';
import {
  ModelStats,
  RunDetail,
  TargetInfo,
  TestResultRow,
  TestsApi,
  fmtCredits,
  fmtPctValue,
  fmtSeconds,
  runIsActive,
  scoreColor,
  useTestsAccess,
} from '../services/testsApi';

type Preset = 'balanced' | 'accuracy' | 'cheapest';

const PRESET_LABELS: Record<Preset, string> = { balanced: 'Balanced', accuracy: 'Most accurate', cheapest: 'Cheapest' };

export default function TestRunPage() {
  const { runId } = useParams<{ runId: string }>();
  const [params] = useSearchParams();
  const { getIdToken } = useAuth();
  const [account, setAccount] = useState<string | null>(params.get('customer_id'));
  const { api, access } = useTestsAccess(getIdToken, account);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [target, setTarget] = useState<TargetInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [preset, setPreset] = useState<Preset>('balanced');
  const [filter, setFilter] = useState<{ model: string; caseId: string; status: string }>({
    model: params.get('model') || '',
    caseId: params.get('case') || '',
    status: '',
  });
  const [makeDefault, setMakeDefault] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [targetKey, setTargetKey] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(() => setReload(r => r + 1), []);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    api
      .run(runId)
      .then(d => {
        if (cancelled) return;
        setDetail(d);
        setError(null);
        // Admins opening another account's run: work in that account so links and actions resolve.
        if (access?.is_admin && d.run.customer_id !== access.account_id && d.run.customer_id !== account) setAccount(d.run.customer_id);
      })
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [api, runId, reload, access, account]);

  const run = detail?.run;
  useEffect(() => {
    if (!run || (access && run.customer_id !== access.account_id)) return;
    let cancelled = false;
    api
      .target(run.target_type, run.target_id)
      .then(d => !cancelled && setTarget(d.target))
      .catch(() => !cancelled && setTarget(null));
    return () => {
      cancelled = true;
    };
    // Only when the target changes, not on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, run?.target_type, run?.target_id, run?.customer_id, access?.account_id, targetKey]);

  const active = runIsActive(run);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [active, refresh]);

  const summary = run?.summary || null;
  const models = useMemo(() => {
    if (!summary) return [] as string[];
    const order = (summary.rankings?.[preset] || []).map(r => r.model);
    const rest = Object.keys(summary.models).filter(m => !order.includes(m));
    return [...order, ...rest];
  }, [summary, preset]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detail || !run) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;

  const bar = summary?.picks?.bar?.accuracy ?? 85;
  const multi = models.length > 1;
  const defaultModel = summary?.default_model || run.default_model;
  const currentDefault = target?.default_model ?? defaultModel;
  const q = account ? `&customer_id=${encodeURIComponent(account)}` : '';
  const targetLink =
    run.target_type === 'chain'
      ? `/dashboard/chain-performance/${run.target_id}?tab=tests${q}`
      : target?.function_id
        ? `/dashboard/function-runs/${target.function_id}?tab=tests${q}`
        : null;
  const p = run.progress || {};
  const caseNames = Object.fromEntries(detail.cases.map(c => [c.id, c.name]));
  const canMakeDefault = run.target_type === 'function' && run.status === 'completed' && !!target;
  const failingOnDefault =
    run.status === 'completed' && defaultModel
      ? detail.cases.filter(c => ['fail', 'partial', 'error'].includes(cellStatus(detail.matrix[c.id]?.[defaultModel]))).length
      : 0;
  const ownRun = !!access && run.customer_id === access.account_id;

  const cancel = async () => {
    try {
      await api.cancelRun(run.id);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.continueRun(run.id);
      setNotice('Finishing the remaining results. Done ones are kept.');
      refresh();
    } catch (e) {
      setNotice(null);
      setActionError(errorText(e));
    } finally {
      setFinishing(false);
    }
  };

  return (
    <Box>
      <PageBreadcrumbs
        items={[
          { label: 'Tests', to: '/dashboard/tests' },
          ...(targetLink ? [{ label: run.target_name || 'Target', to: targetLink }] : [{ label: run.target_name || 'Target' }]),
          { label: 'Run' },
        ]}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 1, mb: 2 }}>
        <Box>
          <Typography variant="h4">{run.target_name || 'Test run'}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
            <RunStatus run={run} />
            <Typography variant="body2" color="text.secondary">
              {run.created_at ? new Date(run.created_at).toLocaleString() : ''} · {models.length || (run.config.models || []).length} model
              {(models.length || (run.config.models || []).length) === 1 ? '' : 's'}
              {run.config.group ? ` · ${run.config.group.name} group` : ''} · {run.config.repeats || 1} run
              {(run.config.repeats || 1) === 1 ? '' : 's'} per case · {fmtCredits(run.credits_used)} credits
              {run.credit_cap ? ` of ${fmtCredits(run.credit_cap)} cap` : ''}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {active && (
            <Button color="warning" variant="outlined" onClick={cancel}>
              Cancel
            </Button>
          )}
          <Button variant="outlined" onClick={() => api.exportRun(run.id, 'csv').catch(e => setError(errorText(e)))}>
            Export CSV
          </Button>
          <Button variant="outlined" onClick={() => api.exportRun(run.id, 'json').catch(e => setError(errorText(e)))}>
            JSON
          </Button>
        </Stack>
      </Box>

      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      {run.remaining ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={finish} disabled={finishing} sx={{ whiteSpace: 'nowrap' }}>
              {finishing ? 'Starting…' : `Finish the remaining ${run.remaining.results}`}
            </Button>
          }
        >
          {p.capped
            ? `Stopped at the ${fmtCredits(run.credit_cap)} credit cap after ${p.done || 0} of ${p.total || '?'} results, so these numbers cover only part of the cases.`
            : `Cancelled after ${p.done || 0} of ${p.total || '?'} results.`}{' '}
          Finishing runs only the missing results, about {fmtCredits(run.remaining.credits)} more credits; nothing is redone.
        </Alert>
      ) : (
        run.error && <Alert severity="error" sx={{ mb: 2 }}>{run.error}</Alert>
      )}
      {active && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            {run.status === 'queued' ? 'Waiting to start…' : `${p.done || 0} of ${p.total || '?'} done`}
            {p.failed ? ` · ${p.failed} errors` : ''}
            {p.skipped ? ` · ${p.skipped} skipped` : ''}
          </Typography>
          <LinearProgress variant={p.total ? 'determinate' : 'indeterminate'} value={p.total ? (100 * (p.done || 0)) / p.total : undefined} />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            You can leave this page; the run keeps going.
          </Typography>
        </Paper>
      )}
      {detail.regressions && (detail.regressions.newly_failing.length > 0 || detail.regressions.newly_passing.length > 0) && (
        <Alert severity={detail.regressions.newly_failing.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Since the{' '}
          <Link component={RouterLink} to={`/dashboard/tests/runs/${detail.regressions.previous_run_id}${account ? `?customer_id=${encodeURIComponent(account)}` : ''}`}>
            previous run
          </Link>{' '}
          on {modelLabel(detail.regressions.model)}:
          {detail.regressions.newly_failing.length > 0 &&
            ` now failing: ${detail.regressions.newly_failing.map(id => caseNames[id] || 'deleted case').join(', ')}.`}
          {detail.regressions.newly_passing.length > 0 &&
            ` now passing: ${detail.regressions.newly_passing.map(id => caseNames[id] || 'deleted case').join(', ')}.`}
        </Alert>
      )}

      {summary && multi && (
        <>
          {summary.recommendation && (
            <Alert severity={summary.recommendation.action === 'switch' ? 'info' : 'success'} sx={{ mb: 2 }}>
              {summary.recommendation.text}
              {summary.recommendation.note ? ` ${summary.recommendation.note}` : ''}
            </Alert>
          )}
          <Box sx={{ mb: 3 }}>
            {canMakeDefault && summary.picks?.recommended && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Button variant="contained" onClick={() => setApplyOpen(true)}>
                  Use these models
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Recommended becomes the default and another pick the first backup. You'll see the change first, and can undo it from the function's
                  Tests tab.
                </Typography>
              </Stack>
            )}
            <PicksCards
              picks={summary.picks}
              current={currentDefault}
              onMakeDefault={canMakeDefault ? setMakeDefault : undefined}
            />
          </Box>
          <SectionTitle
            title="Models"
            help="Accuracy is the 0–100 average score. Passing is the share of cases at or above their pass score."
            action={
              <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => v && setPreset(v)}>
                {(Object.keys(PRESET_LABELS) as Preset[]).map(k => (
                  <ToggleButton key={k} value={k}>
                    {PRESET_LABELS[k]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            }
          />
          <ModelComparisonTable
            rows={rowsFromSummary(summary, models)}
            defaultModel={defaultModel}
            picks={summary.picks}
            deadline={summary.deadline_s}
            bar={bar}
            onRowClick={m => setFilter(f => ({ ...f, model: m }))}
          />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Click a model to see its outputs below.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Tradeoff title="Accuracy vs cost" models={models} stats={summary.models} x={s => s.cost.credits_per_run} xLabel="Credits per run" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Tradeoff title="Accuracy vs speed" models={models} stats={summary.models} x={s => s.speed.p95_s} xLabel="p95 seconds" />
            </Grid>
          </Grid>
        </>
      )}

      {summary && !multi && models[0] && (
        <SingleModel stats={summary.models[models[0]]} model={models[0]} deadline={summary.deadline_s} bar={bar} />
      )}

      {detail.cases.length > 0 && models.length > 0 && (
        <>
          <SectionTitle
            title="By case"
            help="Each tile is a case's average score on a model, coloured by whether it passed. Click a tile to see that model's outputs for the case below."
            action={
              ownRun ? (
                <ImproveWithLearning
                  getToken={getIdToken}
                  targetType={run.target_type}
                  targetId={run.target_id}
                  targetName={run.target_name || 'this target'}
                  failing={failingOnDefault}
                  size="small"
                />
              ) : undefined
            }
          />
          <CaseMatrix
            detail={detail}
            models={models}
            defaultModel={defaultModel}
            selected={filter}
            onSelect={(model, caseId) => {
              setFilter({ model, caseId, status: '' });
              setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            }}
          />
        </>
      )}

      <Box ref={resultsRef} sx={{ scrollMarginTop: 72 }} />
      <Results
        api={api}
        runId={run.id}
        models={models.length ? models : run.config.models || []}
        caseNames={caseNames}
        filter={filter}
        onFilter={setFilter}
        judge={detail.judge_function}
        account={account}
        chain={run.target_type === 'chain'}
        reloadKey={active ? reload : 0}
        onVerdict={refresh}
      />

      {canMakeDefault && target && (
        <ApplyPicksDialog
          open={applyOpen}
          api={api}
          routes={[{ family_id: target.target_id, customer_id: run.customer_id, name: run.target_name || target.name }]}
          call={(fallback, dryRun) => api.applyTargetPicks(target.target_id, fallback, dryRun, run.id)}
          fromRun
          onClose={() => setApplyOpen(false)}
          onApplied={applied => {
            setApplyOpen(false);
            if (applied) setNotice(`Saved: ${run.target_name || target.name} now uses this run's picks. Undo it from the function's Tests tab.`);
            setTargetKey(k => k + 1);
          }}
        />
      )}
      <MakeDefaultDialog
        api={api}
        runId={run.id}
        model={makeDefault}
        current={currentDefault}
        platform={target?.platform_deadline_s !== null && target?.platform_deadline_s !== undefined}
        name={run.target_name}
        onClose={() => setMakeDefault(null)}
        onDone={text => {
          setMakeDefault(null);
          setNotice(text);
          setTargetKey(k => k + 1);
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------

const Tradeoff: React.FC<{
  title: string;
  models: string[];
  stats: Record<string, ModelStats>;
  x: (s: ModelStats) => number | null;
  xLabel: string;
}> = ({ title, models, stats, x, xLabel }) => {
  const points = models
    .map(m => ({ model: modelLabel(m), x: stats[m] ? x(stats[m]) : null, y: stats[m]?.accuracy.score ?? null }))
    .filter((d): d is { model: string; x: number; y: number } => d.x !== null && d.y !== null);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: 300 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        Up and to the left is better.
      </Typography>
      {points.length ? (
        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart margin={{ top: 10, right: 30, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" dataKey="x" name={xLabel} label={{ value: xLabel, position: 'insideBottom', offset: -10, fontSize: 12 }} tick={{ fontSize: 11 }} />
            <YAxis type="number" dataKey="y" name="Accuracy" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <ChartTooltip
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(v: number, name: string) => [typeof v === 'number' ? v.toFixed(name === 'Accuracy' ? 0 : 3) : v, name]}
              labelFormatter={() => ''}
            />
            <Scatter data={points} fill="#0288d1">
              <LabelList dataKey="model" position="top" style={{ fontSize: 10 }} />
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Not enough graded results.
        </Typography>
      )}
    </Paper>
  );
};

const SingleModel: React.FC<{ stats: ModelStats; model: string; deadline: number | null; bar: number }> = ({ stats, model, deadline, bar }) => (
  <Box sx={{ mb: 3 }}>
    <SectionTitle title={modelLabel(model)} help="Compare more models from the Tests tab to get a recommendation." />
    <Grid container spacing={1.5}>
      <Grid item xs={6} md={2.4}>
        <StatCard label="Accuracy" value={<Score value={stats.accuracy.score} bar={bar} />} caption={`${stats.accuracy.graded} graded results`} />
      </Grid>
      <Grid item xs={6} md={2.4}>
        <StatCard label="Passing" value={fmtPctValue(stats.accuracy.pass_rate)} caption={stats.accuracy.critical_failures ? `${stats.accuracy.critical_failures} critical failures` : undefined} />
      </Grid>
      <Grid item xs={6} md={2.4}>
        <StatCard label="Success" value={fmtPctValue(stats.reliability.success_rate)} caption={stats.reliability.schema_valid_rate !== null ? `${fmtPctValue(stats.reliability.schema_valid_rate)} match the schema` : undefined} />
      </Grid>
      <Grid item xs={6} md={2.4}>
        <StatCard
          label="p95 time"
          value={fmtSeconds(stats.speed.p95_s)}
          caption={deadline !== null ? `${fmtPctValue(stats.speed.within_deadline)} within ${deadline}s` : `p50 ${fmtSeconds(stats.speed.p50_s)}`}
        />
      </Grid>
      <Grid item xs={12} md={2.4}>
        <StatCard label="Credits per run" value={fmtCredits(stats.cost.credits_per_run)} caption={`${fmtCredits(stats.cost.credits_total)} total`} />
      </Grid>
    </Grid>
  </Box>
);

// ---------------------------------------------------------------------------

const Results: React.FC<{
  api: TestsApi;
  runId: string;
  models: string[];
  caseNames: Record<string, string>;
  filter: { model: string; caseId: string; status: string };
  onFilter: (f: { model: string; caseId: string; status: string }) => void;
  judge: { function_id: string; name: string } | null;
  account: string | null;
  chain: boolean;
  reloadKey: number;
  onVerdict: () => void;
}> = ({ api, runId, models, caseNames, filter, onFilter, judge, account, chain, reloadKey, onVerdict }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ results: TestResultRow[]; total: number } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const pageSize = 50;

  useEffect(() => setPage(1), [filter]);

  useEffect(() => {
    let cancelled = false;
    api
      .results(runId, { model: filter.model || undefined, case_id: filter.caseId || undefined, status: filter.status || undefined, page, page_size: pageSize })
      .then(d => !cancelled && setData(d))
      .catch(() => !cancelled && setData({ results: [], total: 0 }));
    return () => {
      cancelled = true;
    };
  }, [api, runId, filter, page, reloadKey]);

  const replace = (row: TestResultRow) => setData(d => (d ? { ...d, results: d.results.map(r => (r.id === row.id ? row : r)) } : d));
  const q = account ? `?customer_id=${encodeURIComponent(account)}` : '';

  return (
    <Box>
      <SectionTitle
        title="Results"
        help="Every call, with the judge's reasoning. Disagree? Set your own verdict: it replaces the judge's and teaches the judge function."
        action={
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Model</InputLabel>
              <Select label="Model" value={filter.model} onChange={e => onFilter({ ...filter, model: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                {models.map(m => (
                  <MenuItem key={m} value={m}>
                    {modelLabel(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Show</InputLabel>
              <Select label="Show" value={filter.status} onChange={e => onFilter({ ...filter, status: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="error">Errors</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
              </Select>
            </FormControl>
            {filter.caseId && (
              <Chip label={`Case: ${caseNames[filter.caseId] || 'selected'}`} onDelete={() => onFilter({ ...filter, caseId: '' })} />
            )}
          </Stack>
        }
      />
      {!data ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box>
      ) : !data.results.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No results{filter.model || filter.status || filter.caseId ? ' match' : ' yet'}.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Case</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell>Verdict</TableCell>
                <TableCell align="right">Time</TableCell>
                <TableCell align="right">Credits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map(r => (
                <React.Fragment key={r.id}>
                  <TableRow hover sx={{ cursor: 'pointer', '& > td': { borderBottom: open === r.id ? 'none' : undefined } }} onClick={() => setOpen(o => (o === r.id ? null : r.id))}>
                    <TableCell padding="checkbox">
                      <IconButton size="small">{open === r.id ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}</IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{(r.case_id && caseNames[r.case_id]) || 'Deleted case'}</Typography>
                      {r.repeat_index > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          run {r.repeat_index + 1}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{modelLabel(r.model)}</TableCell>
                    <TableCell align="right">
                      <Score value={r.score} />
                    </TableCell>
                    <TableCell>
                      <VerdictChip row={r} />
                    </TableCell>
                    <TableCell align="right">{r.latency_ms !== null ? `${(r.latency_ms / 1000).toFixed(1)}s` : '–'}</TableCell>
                    <TableCell align="right">{fmtCredits(r.credits)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: open === r.id ? undefined : 'none' }}>
                      <Collapse in={open === r.id} unmountOnExit>
                        <ResultDetail api={api} runId={runId} row={r} judge={judge} q={q} chain={chain} onSaved={row => { replace(row); onVerdict(); }} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          {data.total > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <Pagination count={Math.ceil(data.total / pageSize)} page={page} onChange={(_, v) => setPage(v)} size="small" />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

const VerdictChip: React.FC<{ row: TestResultRow }> = ({ row }) => {
  if (row.status === 'error') return <Chip size="small" color="error" label="error" />;
  if (row.status === 'skipped') return <Chip size="small" label="skipped" />;
  if (row.human_verdict) {
    return (
      <Tooltip title={`Set by ${row.human_verdict.by}${row.human_verdict.note ? `: ${row.human_verdict.note}` : ''}`}>
        <Chip size="small" color={row.human_verdict.verdict === 'pass' ? 'success' : 'error'} label={`${row.human_verdict.verdict} (you)`} />
      </Tooltip>
    );
  }
  if (row.passed === null) return <Chip size="small" variant="outlined" label="not graded" />;
  return <Chip size="small" variant="outlined" color={row.passed ? 'success' : 'error'} label={row.passed ? 'pass' : 'fail'} />;
};

const ResultDetail: React.FC<{
  api: TestsApi;
  runId: string;
  row: TestResultRow;
  judge: { function_id: string; name: string } | null;
  q: string;
  chain: boolean;
  onSaved: (row: TestResultRow) => void;
}> = ({ api, runId, row, judge, q, chain, onSaved }) => {
  const [note, setNote] = useState(row.human_verdict?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const j = row.judge;
  const c = row.checks;

  const setVerdict = async (verdict: 'pass' | 'fail' | null) => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.setVerdict(runId, row.id, verdict, note.trim() || undefined);
      onSaved(r.result);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const runLink = row.run_ref ? (chain ? `/dashboard/chain-run/${row.run_ref}` : `/dashboard/run/${row.run_ref}`) : null;
  const output = row.output === null || row.output === undefined ? '' : typeof row.output === 'string' ? row.output : JSON.stringify(row.output, null, 2);

  return (
    <Box sx={{ py: 1.5 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary">
            Output{row.model_used && row.model_used !== row.model ? ` (served by ${modelLabel(row.model_used)})` : ''}
            {runLink && (
              <>
                {' · '}
                <Link component={RouterLink} to={`${runLink}${q}`}>
                  full run
                </Link>
              </>
            )}
          </Typography>
          {row.error ? (
            <Alert severity="error" sx={{ mt: 0.5 }}>
              {row.error}
            </Alert>
          ) : (
            <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
              {output || '(empty)'}
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {j && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Judge{j.model ? ` (${modelLabel(j.model)})` : ''}
                {j.cached ? ' · reused an earlier grade' : ''}
                {j.run_ref && judge && (
                  <>
                    {' · '}
                    <Link component={RouterLink} to={`/dashboard/run/${j.run_ref}${q}`}>
                      judge run
                    </Link>
                  </>
                )}
              </Typography>
              {j.error ? (
                <Typography variant="body2" color="error">
                  {j.error}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    <Box component="span" sx={{ color: scoreColor(j.score), fontWeight: 600 }}>
                      {j.score ?? '–'}
                    </Box>
                    {j.verdict ? ` · ${j.verdict}` : ''} {j.reason ? `· ${j.reason}` : ''}
                  </Typography>
                  {!!j.missing?.length && <Typography variant="caption" color="error.main" component="div">Missing: {j.missing.join(', ')}</Typography>}
                  {!!j.unexpected?.length && <Typography variant="caption" color="warning.main" component="div">Unexpected: {j.unexpected.join(', ')}</Typography>}
                  {!!j.matched?.length && <Typography variant="caption" color="success.main" component="div">Matched: {j.matched.join(', ')}</Typography>}
                </>
              )}
            </Box>
          )}
          {c && (c.schema?.valid === false || c.fields?.length || c.must_include?.length || c.must_not_include?.length) && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Checks{c.score !== null && c.score !== undefined ? ` · ${Math.round(c.score)}` : ''}
              </Typography>
              {c.schema?.valid === false && (
                <Typography variant="caption" color="error.main" component="div">
                  Doesn't match the output schema: {(c.schema.problems || []).slice(0, 3).join('; ')}
                </Typography>
              )}
              {(c.must_include || []).map((m: { text: string; found: boolean }) => (
                <Typography key={`i-${m.text}`} variant="caption" component="div" color={m.found ? 'success.main' : 'error.main'}>
                  {m.found ? '✓' : '✗'} includes "{m.text}"
                </Typography>
              ))}
              {(c.must_not_include || []).map((m: { text: string; found: boolean }) => (
                <Typography key={`n-${m.text}`} variant="caption" component="div" color={m.found ? 'error.main' : 'success.main'}>
                  {m.found ? '✗' : '✓'} leaves out "{m.text}"
                </Typography>
              ))}
              {(c.fields || []).map((f: { path: string; op: string; value: unknown; ok: boolean; actual?: unknown; note?: string }, i: number) => (
                <Typography key={`f-${i}`} variant="caption" component="div" color={f.ok ? 'success.main' : 'error.main'}>
                  {f.ok ? '✓' : '✗'} {f.path} {f.op} {JSON.stringify(f.value)}
                  {f.actual !== undefined ? ` (got ${JSON.stringify(f.actual)})` : f.note ? ` (${f.note})` : ''}
                </Typography>
              ))}
            </Box>
          )}
          {row.status === 'ok' && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Your verdict
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
              <TextField size="small" fullWidth placeholder="Why? (optional, helps the judge learn)" value={note} onChange={e => setNote(e.target.value)} sx={{ mb: 1 }} />
              <Stack direction="row" spacing={1}>
                <Button size="small" variant={row.human_verdict?.verdict === 'pass' ? 'contained' : 'outlined'} color="success" disabled={saving} onClick={() => setVerdict('pass')}>
                  Pass
                </Button>
                <Button size="small" variant={row.human_verdict?.verdict === 'fail' ? 'contained' : 'outlined'} color="error" disabled={saving} onClick={() => setVerdict('fail')}>
                  Fail
                </Button>
                {row.human_verdict && (
                  <Button size="small" disabled={saving} onClick={() => setVerdict(null)}>
                    Use the judge's
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const MakeDefaultDialog: React.FC<{
  api: TestsApi;
  runId: string;
  model: string | null;
  current: string | null;
  platform: boolean;
  name: string | null;
  onClose: () => void;
  onDone: (text: string) => void;
}> = ({ api, runId, model, current, platform, name, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [model]);
  const save = async () => {
    if (!model) return;
    setSaving(true);
    try {
      const r = await api.makeDefault(runId, model);
      onDone(r.changed ? `${modelLabel(r.default_model)} is now the default for ${name || 'this function'}${r.version ? ` (version ${r.version})` : ''}.` : 'That model was already the default.');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!model} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make {modelLabel(model)} the default?</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2">
            Saves a new version of {name || 'this function'} that uses {modelLabel(model)} instead of {modelLabel(current)}. The previous
            version stays in its history.
          </Typography>
          {platform && (
            <Alert severity="warning">
              This is a platform route. Every platform request for it will use the new model right away.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={platform ? 'warning' : 'primary'} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Make default'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
